import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TrendingUp, TrendingDown, Clock, Play, BarChart2, Target, ShieldAlert, Download } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { toast } from 'sonner'

interface SignalDetail {
  timestamp: string
  score: number
  signal_class: string
  direction: string
  regime: string
  adx_phase: string
  adx_raw: number
  htf_direction?: string
  htf_adx?: number
  entry_price: number
  sl_price: number
  tp_price: number
  outcome: 'HIT' | 'SL' | 'TIMEOUT'
  candles_to_hit: number
  outcome_pts: number
}

interface BreakdownItem {
  totale: number
  hit: number
  hit_rate: string
}

interface RandomBaseline {
  total: number
  random_hit_pct: number
  random_sl_pct: number
  random_timeout_pct: number
  note: string
}

interface BacktestResult {
  periodo_mesi: number
  soglia_score: number
  tp_ratio?: number
  segnali_totali: number
  note: string
  hit_target: number
  hit_sl: number
  no_outcome_5h: number
  hit_rate_pct: number
  per_class: Record<string, BreakdownItem>
  per_adx_phase: Record<string, BreakdownItem>
  per_regime: Record<string, BreakdownItem>
  per_hour_utc: Record<string, BreakdownItem>
  per_htf_direction?: Record<string, BreakdownItem>
  avg_pts_on_hit: number
  avg_candles_to_hit: number
  avg_score_hits: number
  avg_score_sl: number
  random_baseline?: RandomBaseline
  alpha_vs_random?: number
  signals_detail: SignalDetail[]
}

const CLASS_COLORS: Record<string, string> = {
  STRONG: 'bg-emerald-600 text-white',
  GOOD:   'bg-blue-600 text-white',
  WEAK:   'bg-yellow-600 text-white',
  INVALID:'bg-slate-600 text-white',
}
const OUTCOME_COLORS: Record<string, string> = {
  HIT:     'bg-emerald-600 text-white',
  SL:      'bg-red-600 text-white',
  TIMEOUT: 'bg-slate-500 text-white',
}

function HitRateBar({ rate, hits, total }: { rate: string; hits: number; total: number }) {
  const pct = parseFloat(rate) || 0
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all ${pct >= 60 ? 'bg-emerald-500' : pct >= 45 ? 'bg-blue-500' : 'bg-red-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-slate-300 w-20 text-right">{rate} ({hits}/{total})</span>
    </div>
  )
}

export function BacktestDashboard() {
  const { token } = useAuthStore()
  const [months, setMonths] = useState(3)
  const [threshold, setThreshold] = useState(0.65)
  const [tpRatio, setTpRatio] = useState(4.0)
  const [scenarioFilter, setScenarioFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [elapsedSec, setElapsedSec] = useState(0)
  const [result, setResult] = useState<BacktestResult | null>(null)
  const [filter, setFilter] = useState<string>('ALL')
  const [search, setSearch] = useState('')

  async function runBacktest() {
    setLoading(true)
    setResult(null)
    setElapsedSec(0)
    try {
      // 1) POST avvia il job in background, ritorna subito con job_id
      const scenarioParam = scenarioFilter ? `&scenario_filter=${scenarioFilter}` : ''
      const startResp = await fetch(
        `/api/v1/ixen-system/backtest/run?months=${months}&score_threshold=${threshold}&tp_ratio=${tpRatio}${scenarioParam}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      )
      if (!startResp.ok) {
        const err = await startResp.json().catch(() => ({}))
        throw new Error(err.detail || `Errore avvio job ${startResp.status}`)
      }
      const startData = await startResp.json()
      const jobId: string = startData.job_id
      if (!jobId) throw new Error('Job ID non ricevuto dal backend')

      toast.info(`Backtest avviato (job ${jobId.slice(0, 8)}...) — calcolo in background`)

      // 2) Polling ogni 3 secondi finché lo status non è done o error
      const POLL_INTERVAL_MS = 3000
      const MAX_POLL_SEC = 600  // 10 minuti hard cap
      const t0 = Date.now()

      while (true) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
        const elapsed = Math.round((Date.now() - t0) / 1000)
        setElapsedSec(elapsed)

        if (elapsed > MAX_POLL_SEC) {
          throw new Error(`Timeout polling dopo ${MAX_POLL_SEC}s — il backtest potrebbe essere ancora in esecuzione lato server`)
        }

        const statusResp = await fetch(
          `/api/v1/ixen-system/backtest/status/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (!statusResp.ok) {
          const err = await statusResp.json().catch(() => ({}))
          throw new Error(err.detail || `Errore polling status ${statusResp.status}`)
        }
        const statusData = await statusResp.json()

        if (statusData.status === 'done') {
          const data: BacktestResult = statusData.result
          setResult(data)
          toast.success(`Backtest completato in ${statusData.elapsed_sec}s — ${data.segnali_totali} segnali analizzati`)
          break
        } else if (statusData.status === 'error') {
          const errMsg = statusData.error || 'Errore sconosciuto'
          throw new Error(`Backtest fallito lato server: ${errMsg}`)
        }
        // status === 'running' → continua polling
      }
    } catch (e: any) {
      toast.error(`Backtest fallito: ${e.message}`)
    } finally {
      setLoading(false)
      setElapsedSec(0)
    }
  }

  function downloadReport() {
    if (!result) return
    const now = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-')
    const filename = `backtest_${result.periodo_mesi}mesi_${now}.json`
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    toast.success(`Report scaricato: ${filename}`)
  }

  const filteredSignals = (result?.signals_detail ?? []).filter(s => {
    if (filter !== 'ALL' && s.outcome !== filter) return false
    if (search && !s.timestamp.includes(search) && !s.direction.includes(search.toUpperCase()) && !s.signal_class.includes(search.toUpperCase())) return false
    return true
  }) ?? []

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Backtest Engine</h1>
        <p className="text-slate-400 text-sm mt-1">
          Validazione storica del Signal Engine su XAUUSD — MT5 Demo MexAtlantic
        </p>
      </div>

      {/* Configurazione */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-blue-400" />
            Parametri Backtest
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-slate-300 block mb-2">
                Periodo storico: <span className="text-blue-400 font-semibold">{months} {months === 1 ? 'mese' : 'mesi'}</span>
              </label>
              <input
                type="range" min={1} max={12} step={1}
                value={months}
                onChange={e => setMonths(+e.target.value)}
                className="w-full accent-blue-500"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>1 mese</span><span>6 mesi</span><span>12 mesi</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 block mb-2">
                Soglia score: <span className="text-blue-400 font-semibold">{threshold.toFixed(2)}</span>
              </label>
              <input
                type="range" min={0.56} max={0.85} step={0.01}
                value={threshold}
                onChange={e => setThreshold(+e.target.value)}
                className="w-full accent-blue-500"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>0.56 WEAK</span><span>0.65 GOOD</span><span>0.75 STRONG</span>
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-300 block mb-2">
              Ratio TP/SL: <span className="text-blue-400 font-semibold">1:{tpRatio.toFixed(2)}</span>
              {tpRatio !== 4.0 && (
                <span className="ml-2 text-xs text-amber-400">⚠ Override (default live: 1:4)</span>
              )}
            </label>
            <input
              type="range" min={1.5} max={4.0} step={0.25}
              value={tpRatio}
              onChange={e => setTpRatio(+e.target.value)}
              className="w-full accent-blue-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>1:1.5 aggressivo</span>
              <span>1:2.5 bilanciato</span>
              <span>1:4 conservativo (cap prop)</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm text-slate-300 whitespace-nowrap">Filtro Scenario (Yomi v4):</label>
              <div className="flex gap-1.5 flex-wrap">
                {[
                  { value: '',                label: 'Tutti',          color: 'bg-slate-600 hover:bg-slate-500' },
                  { value: 'IMPULSE',         label: 'IMPULSE',        color: 'bg-purple-700 hover:bg-purple-600' },
                  { value: 'TREND_PULLBACK',  label: 'TREND_PULLBACK', color: 'bg-amber-700 hover:bg-amber-600' },
                  { value: 'SQUEEZE',         label: 'SQUEEZE',        color: 'bg-sky-700 hover:bg-sky-600' },
                  { value: 'RANGE',           label: 'RANGE',          color: 'bg-emerald-700 hover:bg-emerald-600' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setScenarioFilter(opt.value)}
                    className={`px-3 py-1 rounded text-xs font-semibold text-white transition-colors ${opt.color} ${scenarioFilter === opt.value ? 'ring-2 ring-white/40' : 'opacity-70'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={runBacktest}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              {loading ? `Calcolo in corso... ${elapsedSec}s` : 'Avvia Backtest'}
            </Button>
            {result && (
              <Button
                onClick={downloadReport}
                variant="outline"
                className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Scarica JSON
              </Button>
            )}
            {loading && (
              <p className="text-slate-400 text-sm animate-pulse">
                Job avviato in background — polling stato ogni 3s. Il calcolo può richiedere 30-180 secondi.
              </p>
            )}
          </div>
          {result && (
            <div className="space-y-1.5">
              {result.tp_ratio !== undefined && (
                <p className="text-xs text-blue-300 bg-blue-900/20 border border-blue-700 rounded px-3 py-2">
                  Ratio TP/SL utilizzato: <span className="font-bold">1:{result.tp_ratio.toFixed(2)}</span>
                  {result.tp_ratio !== 4.0 && <span className="ml-2 text-amber-400">(override del default live 1:4)</span>}
                </p>
              )}
              <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-700 rounded px-3 py-2">
                Nota: {result.note}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400">Hit Rate</p>
                <p className={`text-2xl font-bold mt-1 ${result.hit_rate_pct >= 60 ? 'text-emerald-400' : result.hit_rate_pct >= 45 ? 'text-blue-400' : 'text-red-400'}`}>
                  {result.hit_rate_pct}%
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400">Segnali Totali</p>
                <p className="text-2xl font-bold text-white mt-1">{result.segnali_totali}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 flex items-center gap-1"><Target className="w-3 h-3 text-emerald-400" />Target</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{result.hit_target}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 flex items-center gap-1"><ShieldAlert className="w-3 h-3 text-red-400" />SL Colpiti</p>
                <p className="text-2xl font-bold text-red-400 mt-1">{result.hit_sl}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400">Avg pts/hit</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{result.avg_pts_on_hit}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400">Avg candle/hit</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{result.avg_candles_to_hit} M15</p>
              </CardContent>
            </Card>
          </div>

          {/* Alpha vs Random Baseline */}
          {result.random_baseline && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-purple-400" />
                  Alpha vs Random Baseline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-1">Signal Engine</p>
                    <p className={`text-2xl font-bold ${result.hit_rate_pct >= 60 ? 'text-emerald-400' : result.hit_rate_pct >= 45 ? 'text-blue-400' : 'text-red-400'}`}>
                      {result.hit_rate_pct}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-1">Random Baseline</p>
                    <p className="text-2xl font-bold text-slate-300">{result.random_baseline.random_hit_pct}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-1">Alpha</p>
                    <p className={`text-2xl font-bold ${(result.alpha_vs_random ?? 0) >= 10 ? 'text-emerald-400' : (result.alpha_vs_random ?? 0) >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {(result.alpha_vs_random ?? 0) > 0 ? '+' : ''}{result.alpha_vs_random}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-1">Ratio TP/SL</p>
                    <p className="text-sm text-slate-300 mt-1">{result.random_baseline.note}</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Random SL: {result.random_baseline.random_sl_pct}% · Timeout: {result.random_baseline.random_timeout_pct}% su {result.random_baseline.total.toLocaleString()} segnali simulati
                </div>
              </CardContent>
            </Card>
          )}

          {/* Breakdown tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Per class */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Per Signal Class</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(result.per_class ?? {}).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1">
                      <Badge className={CLASS_COLORS[k] || 'bg-slate-600 text-white'}>{k}</Badge>
                    </div>
                    <HitRateBar rate={v.hit_rate} hits={v.hit} total={v.totale} />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Per ADX phase */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Per ADX Phase</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(result.per_adx_phase ?? {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs text-slate-400 mb-1">{k}</p>
                    <HitRateBar rate={v.hit_rate} hits={v.hit} total={v.totale} />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Per regime */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Per Regime</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(result.per_regime ?? {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs text-slate-400 mb-1">{k}</p>
                    <HitRateBar rate={v.hit_rate} hits={v.hit} total={v.totale} />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Per ora UTC */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300 flex items-center gap-1"><Clock className="w-3 h-3" />Per Fascia Oraria UTC</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {Object.entries(result.per_hour_utc ?? {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs text-slate-400 mb-1">{k}</p>
                    <HitRateBar rate={v.hit_rate} hits={v.hit} total={v.totale} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* HTF Direction breakdown */}
          {result.per_htf_direction && Object.keys(result.per_htf_direction).length > 0 && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-400" />
                  Hit Rate per Direzione H4 (HTF Filter)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {Object.entries(result.per_htf_direction).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          k === 'BULLISH' ? 'bg-emerald-700 text-white' :
                          k === 'BEARISH' ? 'bg-red-700 text-white' :
                          'bg-slate-600 text-white'
                        }`}>{k}</span>
                        <span className="text-xs text-slate-400">H4</span>
                      </div>
                      <HitRateBar rate={v.hit_rate} hits={v.hit} total={v.totale} />
                      <p className="text-xs text-slate-500 mt-1">{v.totale} segnali M15 allineati</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  NEUTRAL = H4 ADX &lt; 20, nessun trend dominante (entrambe le direzioni accettate)
                </p>
              </CardContent>
            </Card>
          )}

          {/* Dettaglio segnali */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-white text-base">
                  Dettaglio Segnali ({filteredSignals.length} / {result.signals_detail?.length ?? 0})
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  {['ALL', 'HIT', 'SL', 'TIMEOUT'].map(f => (
                    <Button
                      key={f}
                      size="sm"
                      variant={filter === f ? 'default' : 'outline'}
                      className={filter === f ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 text-slate-300'}
                      onClick={() => setFilter(f)}
                    >
                      {f}
                    </Button>
                  ))}
                  <input
                    type="text"
                    placeholder="Cerca..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-1 w-36 focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-900 z-10">
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400 text-xs whitespace-nowrap">Timestamp (UTC)</TableHead>
                      <TableHead className="text-slate-400 text-xs">Score</TableHead>
                      <TableHead className="text-slate-400 text-xs">Class</TableHead>
                      <TableHead className="text-slate-400 text-xs">Dir.</TableHead>
                      <TableHead className="text-slate-400 text-xs">Regime</TableHead>
                      <TableHead className="text-slate-400 text-xs">ADX</TableHead>
                      <TableHead className="text-slate-400 text-xs">Entry</TableHead>
                      <TableHead className="text-slate-400 text-xs">SL</TableHead>
                      <TableHead className="text-slate-400 text-xs">TP</TableHead>
                      <TableHead className="text-slate-400 text-xs">Outcome</TableHead>
                      <TableHead className="text-slate-400 text-xs">Candle</TableHead>
                      <TableHead className="text-slate-400 text-xs">Pts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSignals.slice(0, 500).map((s, i) => (
                      <TableRow key={i} className="border-slate-700 hover:bg-slate-700/30 text-xs">
                        <TableCell className="text-slate-300 whitespace-nowrap font-mono text-[11px]">
                          {s.timestamp.replace('T', ' ').slice(0, 16)}
                        </TableCell>
                        <TableCell className="text-slate-200 font-mono">{s.score.toFixed(3)}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] px-1 py-0 ${CLASS_COLORS[s.signal_class] || 'bg-slate-600 text-white'}`}>
                            {s.signal_class}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {s.direction === 'BULLISH'
                            ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                            : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
                        </TableCell>
                        <TableCell className="text-slate-400 text-[11px]">{s.regime}</TableCell>
                        <TableCell className="text-slate-400 font-mono">{s.adx_raw.toFixed(1)}</TableCell>
                        <TableCell className="text-white font-mono">{s.entry_price.toFixed(2)}</TableCell>
                        <TableCell className="text-red-400 font-mono">{s.sl_price.toFixed(2)}</TableCell>
                        <TableCell className="text-emerald-400 font-mono">{s.tp_price.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] px-1 py-0 ${OUTCOME_COLORS[s.outcome] || 'bg-slate-600 text-white'}`}>
                            {s.outcome}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400 font-mono">{s.candles_to_hit}</TableCell>
                        <TableCell className={`font-mono ${s.outcome_pts > 0 ? 'text-emerald-400' : s.outcome_pts < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                          {s.outcome_pts !== 0 ? s.outcome_pts.toFixed(1) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredSignals.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-slate-500 py-8">
                          Nessun segnale con i filtri selezionati
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {filteredSignals.length > 500 && (
                  <p className="text-center text-slate-500 text-xs py-3">
                    Mostrati 500 / {filteredSignals.length} segnali
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
